import { createRouter, createWebHistory } from "vue-router";
import store from "@/store";

const routes = [
  {
    path: "/login",
    name: "Login",
    component: () => import("../views/LoginView.vue"),
    meta: { public: true },
  },
  {
    path: "/",
    component: () => import("../layouts/MainLayout.vue"),
    redirect: "/dashboard",
    children: [
      // Dashboard
      {
        path: "dashboard",
        name: "Dashboard",
        component: () => import("../modules/dashboard/DashboardPage.vue"),
      },
      // Customers
      {
        path: "customers",
        name: "Customers",
        component: () => import("../modules/customers/views/CustomerList.vue"),
        meta: { permission: "customers:view" },
      },
      {
        path: "customers/:id",
        name: "CustomerDetail",
        component: () =>
          import("../modules/customers/views/CustomerDetail.vue"),
        meta: {
          permission: "customers:view",
          breadcrumb: [
            { label: 'Customers', to: '/customers' },
            { label: 'Customer Detail' },
          ],
        },
      },
      // Invoices
      {
        path: "invoices",
        name: "Invoices",
        component: () => import("../modules/invoices/InvoiceList.vue"),
        meta: { permission: "invoices:view" },
      },
      // [CMS-DISABLED] Invoice create route disabled per CMS Change Request 2026-06-07
      // Invoices are now imported via Invoice Upload module.
      // {
      //   path: "invoices/new",
      //   name: "InvoiceCreate",
      //   component: () => import("../modules/invoices/InvoiceForm.vue"),
      //   meta: {
      //     permission: "invoices:create",
      //     breadcrumb: [
      //       { label: 'Invoices', to: '/invoices' },
      //       { label: 'New Invoice' },
      //     ],
      //   },
      // },
      {
        path: "invoices/:id",
        name: "InvoiceDetail",
        component: () => import("../modules/invoices/InvoiceDetail.vue"),
        meta: {
          permission: "invoices:view",
          breadcrumb: [
            { label: 'Invoices', to: '/invoices' },
            { label: 'Invoice Detail' },
          ],
        },
      },
      {
        path: "invoice-uploads",
        name: "InvoiceUploads",
        component: () => import("../modules/invoices/InvoiceUploadView.vue"),
        meta: {
          permission: "invoice_upload:view",
          breadcrumb: [
            { label: 'Invoices', to: '/invoices' },
            { label: 'Upload Invoices' },
          ],
        },
      },
      // Payments
      {
        path: "payments",
        name: "Payments",
        component: () => import("../modules/payments/PaymentList.vue"),
        meta: { permission: "payments:view" },
      },
      // [CMS-DEPRECATED] Payment create route deprecated per CMS Change Request 2026-06-07
      // New collections entries must be created via the Receipts module.
      // {
      //   path: "payments/new",
      //   name: "PaymentCreate",
      //   component: () => import("../modules/payments/PaymentForm.vue"),
      //   meta: {
      //     permission: "payments:create",
      //     breadcrumb: [
      //       { label: 'Payments', to: '/payments' },
      //       { label: 'New Payment' },
      //     ],
      //   },
      // },
      // Receipts
      {
        path: "receipts",
        name: "Receipts",
        component: () => import("../modules/receipts/ReceiptList.vue"),
        meta: { permission: "receipts:view" },
      },
      {
        path: "receipts/new",
        name: "ReceiptCreate",
        component: () => import("../modules/receipts/ReceiptForm.vue"),
        meta: {
          permission: "receipts:create",
          breadcrumb: [
            { label: 'Receipts', to: '/receipts' },
            { label: 'New Receipt' },
          ],
        },
      },
      {
        path: "receipts/:id",
        name: "ReceiptDetail",
        component: () => import("../modules/receipts/ReceiptDetail.vue"),
        meta: {
          permission: "receipts:view",
          breadcrumb: [
            { label: 'Receipts', to: '/receipts' },
            { label: 'Receipt Detail' },
          ],
        },
      },
      // Finance / Knock-off
      {
        path: "finance/knockoff/:receiptId",
        name: "KnockoffView",
        component: () => import("../modules/finance/KnockoffView.vue"),
        meta: { permission: "payments:create" },
      },
      {
        path: "knockoff-uploads",
        name: "KnockoffUploads",
        component: () => import("../modules/finance/KnockoffUploadView.vue"),
        meta: {
          permission: "knockoff_upload:view",
          breadcrumb: [
            { label: 'Finance', to: '/receipts' },
            { label: 'Upload Knock-offs' },
          ],
        },
      },
      // Collections
      {
        path: "collections",
        name: "Collections",
        component: () => import("../views/CollectionsView.vue"),
        meta: { permission: "collections:view" },
      },
      // Orders
      {
        path: "orders",
        name: "Orders",
        component: () => import("../views/OrdersView.vue"),
        meta: { permission: "orders:view" },
      },
      // Sales Orders
      {
        path: "sales-orders",
        name: "SalesOrders",
        component: () => import("../modules/salesOrders/SalesOrdersView.vue"),
        meta: { permission: "salesOrders:view" },
      },
      // Delivery Challans
      {
        path: "deliveries",
        name: "Deliveries",
        component: () => import("../views/DeliveriesView.vue"),
        meta: { permission: "delivery:view" },
      },
      // Reports
      {
        path: "reports",
        name: "Reports",
        component: () => import("../views/ReportsView.vue"),
        meta: { permission: "reports:view" },
      },
      // Followups
      {
        path: "followups",
        name: "Followups",
        component: () => import("../modules/followups/FollowupList.vue"),
        meta: { permission: "followups:view" },
      },
      // Reminders
      {
        path: "reminders",
        name: "Reminders",
        component: () => import("../modules/reminders/ReminderList.vue"),
        meta: { permission: "reminders:view" },
      },
      // Workflow
      {
        path: "workflow",
        name: "Workflow",
        component: () => import("../modules/workflow/WorkflowList.vue"),
      },
      // Notifications
      {
        path: "notifications",
        name: "Notifications",
        component: () =>
          import("../modules/notifications/NotificationList.vue"),
      },
      // Settings
      {
        path: "settings/users",
        name: "Users",
        component: () => import("../views/UsersView.vue"),
        meta: { permission: "users:view" },
      },
      {
        path: "settings/roles",
        name: "Roles",
        component: () => import("../views/RolesView.vue"),
        meta: { permission: "roles:manage" },
      },
      {
        path: "settings/security",
        name: "SecurityDashboard",
        component: () => import("../views/SecurityDashboardView.vue"),
        meta: { permission: "reports:view" },
      },
      {
        path: "settings/legal-entities",
        name: "LegalEntities",
        component: () =>
          import("../modules/master/views/LegalEntitiesView.vue"),
        meta: { permission: "roles:manage" },
      },
      // --- MDM Admin: Currency Master ---
      {
        path: "settings/currencies",
        name: "CurrencyMaster",
        component: () =>
          import("../modules/master/views/CurrencyMasterView.vue"),
        meta: {
          permission: "master:view",
          breadcrumb: [
            { label: 'Settings', to: '/settings/users' },
            { label: 'Currency Master' },
          ],
        },
      },
      // --- MDM Admin: Country Master ---
      {
        path: "settings/countries",
        name: "CountryMaster",
        component: () =>
          import("../modules/master/views/CountryMasterView.vue"),
        meta: {
          permission: "master:view",
          breadcrumb: [
            { label: 'Settings', to: '/settings/users' },
            { label: 'Country Master' },
          ],
        },
      },
      // --- MDM Admin: Dynamic Object Manager ---
      {
        path: "settings/dynamic-fields",
        name: "DynamicObjectManager",
        component: () =>
          import("../modules/master/views/DynamicObjectManagerView.vue"),
        meta: {
          permission: "metadata:edit",
          breadcrumb: [
            { label: 'Settings', to: '/settings/users' },
            { label: 'Dynamic Fields' },
          ],
        },
      },
      // --- MDM Admin: Period Lock Manager ---
      {
        path: "settings/period-locks",
        name: "PeriodLockManager",
        component: () =>
          import("../modules/master/views/PeriodLockManagerView.vue"),
        meta: {
          permission: "periodLock:view",
          breadcrumb: [
            { label: 'Settings', to: '/settings/users' },
            { label: 'Period Locks' },
          ],
        },
      },
      // --- Bank Account Master (CMS Change Request 2026-06-07) ---
      {
        path: "settings/bank-accounts",
        name: "BankAccountMaster",
        component: () =>
          import("../modules/master/views/BankAccountsView.vue"),
        meta: {
          permission: "bank_accounts:view",
          breadcrumb: [
            { label: 'Settings', to: '/settings/users' },
            { label: 'Bank Accounts' },
          ],
        },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach((to, from, next) => {
  const isAuthenticated = store.getters["auth/isAuthenticated"];

  if (to.meta.public) {
    if (isAuthenticated) return next({ name: "Dashboard" });
    return next();
  }

  if (!isAuthenticated) {
    return next({ name: "Login" });
  }

  const requiredPermission = to.meta.permission;
  if (requiredPermission) {
    const hasPerm = store.getters["auth/hasPermission"](requiredPermission);
    if (!hasPerm) {
      // Prevent infinite loops if fallback lacks permission
      if (to.name === "Dashboard") return next("/");
      return next({ name: "Dashboard" });
    }
  }

  next();
});

export default router;
